import Group from 'components/utils/Group'
import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import DoublePointTakeoverLogo from './DoublePointTakeoverLogo'
import TextLink from 'LuxKit/TextLink'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { useAppSelector } from 'hooks/reduxHooks'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import styled from 'styled-components'
import useDoublePointsCampaignTier from '../hooks/useDoublePointsCampaignTier'
import LuxPlusExperienceDiscountOfferBanner from 'takeovers/luxplusExperienceDiscountTakeover/components/LuxPlusExperienceDiscountOfferBanner'
import { EXPERIENCE_ENABLED_REGIONS } from '../constants/societeDoublePointsTakeoverConstants'
import { getThemeClassName } from 'lib/theme/themeUtils'

const TextContent = styled.div`
  &.bronze {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &.silver {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &.gold {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  &.platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

function DoublePointTakeoverBannerContent() {
  const intl = useIntl()
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isExperienceEnabledRegion = EXPERIENCE_ENABLED_REGIONS.has(currentRegionCode)

  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const [tier, tierName, , nextTierName] = useCurrentLoyaltyTier()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const displayTierName = nextTierName ?? tierName
  const displayTier = useDoublePointsCampaignTier()

  const firstSentence = useMemo(() => {
    if (hasLoyaltyAccount) {
      if (tier === 'platinum') {
        return intl.formatMessage(
          {
            id: '0r6Ax5c4XYtDoa60JS0H',
            defaultMessage: 'Extend your {displayTierName} {loyaltyProgramName} status',
            description: 'Banner heading for platinum members to extend their loyalty status',
          },
          { displayTierName, loyaltyProgramName },
        )
      }
      return intl.formatMessage(
        {
          id: 'Wg_8qfr3R90XaJo0ca-B',
          defaultMessage: 'Fast-track to {displayTierName} {loyaltyProgramName} status',
          description: 'Banner heading for members to fast-track to next loyalty tier',
        },
        { displayTierName, loyaltyProgramName },
      )
    }
    return intl.formatMessage(
      {
        id: 'EJ0fFbVvs-FwO3Rae1Sg',
        defaultMessage: 'Fast-track your {loyaltyProgramName} status',
        description: 'Banner heading for non-members to fast-track their loyalty status',
      },
      { loyaltyProgramName },
    )
  }, [hasLoyaltyAccount, displayTierName, loyaltyProgramName, tier, intl])

  // Show LuxPlus Experience Discount banner for regions where experiences are available
  if (isExperienceEnabledRegion) {
    return <LuxPlusExperienceDiscountOfferBanner withCta />
  }

  return (
    <Group direction="horizontal" gap={12} verticalAlign="center" className={getThemeClassName({ mobile: 'default' })}>
      <DoublePointTakeoverLogo size="small" />
      <TextContent className={displayTier}>
        <CSSBreakpoint max="mobile">
          <Group direction="vertical" gap={0} verticalAlign="start">
            <b>{firstSentence}</b>
            <span>
              {intl.formatMessage(
                {
                  id: '6eC8Xs9KqoaH_tVk18SO',
                  defaultMessage: 'Double status credits on all hotels, tours and cruises. Limited time only. <link>View all offers</link>',
                  description: 'Mobile banner description about double status credits with link to view all offers',
                },
                {
                  link: (chunks: React.ReactNode) => (
                    <TextLink wrap="no-wrap" weight="regular" to="/double-status-credits" colour="inherit">
                      {chunks}
                    </TextLink>
                  ),
                },
              )}
            </span>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          {intl.formatMessage(
            {
              id: 'uKyBA7nk81xKszJ7XyVh',
              defaultMessage: '<bold>{heading}</bold> Double status credits on all hotels, tours and cruises. Limited time only. <link>View all offers</link>',
              description: 'Tablet banner with heading, description and link for double status credits',
            },
            {
              heading: firstSentence,
              bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
              link: (chunks: React.ReactNode) => (
                <TextLink wrap="no-wrap" to="/double-status-credits" colour="inherit">
                  {chunks}
                </TextLink>
              ),
            },
          )}
        </CSSBreakpoint>
      </TextContent>
    </Group>
  )
}

export default DoublePointTakeoverBannerContent
